import type { Resume } from "@adafia/types";
import { Button, Modal, useToast } from "@adafia/ui";
import {
	type DragEndEvent,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import { arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	ChevronLeft,
	Cloud,
	CloudOff,
	Download,
	Save,
	Settings,
	User,
} from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getResumeBySlug, updateResume } from "../api/resume";
import { CertificationEditor } from "./Editor/CertificationEditor";
import { EducationEditor } from "./Editor/EducationEditor";
import { ExperienceEditor } from "./Editor/ExperienceEditor";
import { InputGroup } from "./Editor/InputGroup";
import { ProjectEditor } from "./Editor/ProjectEditor";
import { SectionHeader } from "./Editor/SectionHeader";
import { SkillEditor } from "./Editor/SkillEditor";
import { TextAreaGroup } from "./Editor/TextAreaGroup";
import { ResumePreview } from "./Preview/ResumePreview";

export function ResumeEditor() {
	const { showToast } = useToast();
	const { slug } = useParams<{ slug: string }>();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const [isSettingsOpen, setIsSettingsOpen] = useState(false);
	const [isSkillModalOpen, setIsSkillModalOpen] = useState(false);
	const [newSkillName, setNewSkillName] = useState("");

	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [deleteAction, setDeleteAction] = useState<{
		id: string;
		type: "experience" | "education" | "projects" | "certifications" | "skills";
	} | null>(null);

	const [editName, setEditName] = useState("");
	const [editSlug, setEditSlug] = useState("");
	const [editIsPublished, setEditIsPublished] = useState(false);

	const [localResume, setLocalResume] = useState<Resume | null>(null);
	const [isDirty, setIsDirty] = useState(false);
	const [isAutoSaveEnabled, setIsAutoSaveEnabled] = useState<boolean>(() => {
		const saved = localStorage.getItem("resume-autosave-enabled");
		return saved !== null ? saved === "true" : true;
	});

	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	);

	const {
		data: resume,
		isLoading,
		error,
	} = useQuery({
		queryKey: ["resume", slug],
		queryFn: () => getResumeBySlug(slug ?? ""),
		enabled: !!slug,
	});

	const lastResumeId = useRef<string | null>(null);

	useEffect(() => {
		if (resume) {
			const currentId = resume.id ?? null;
			const isDifferentResume = currentId !== lastResumeId.current;
			if (isDifferentResume || !isDirty) {
				setLocalResume(resume);
				setIsDirty(false);
				lastResumeId.current = currentId;
			}
		}
	}, [resume, isDirty]);

	const mutation = useMutation({
		mutationFn: (data: Partial<Resume>) => {
			if (!resume?.id) throw new Error("Resume ID is missing");
			return updateResume(resume.id, data);
		},
		onSuccess: (updated) => {
			queryClient.setQueryData(["resume", updated.slug], updated);
			if (updated.slug !== slug) {
				navigate(`/${updated.slug}`, { replace: true });
			}
			setIsDirty(false);
			showToast("Changes saved", "success");
		},
		onError: () => {
			showToast("Failed to save", "error");
		},
	});

	const handleSave = useCallback(() => {
		if (localResume && isDirty) {
			mutation.mutate(localResume);
		}
	}, [localResume, isDirty, mutation]);

	// Auto-save logic
	useEffect(() => {
		if (isAutoSaveEnabled && isDirty) {
			const timer = setTimeout(() => {
				handleSave();
			}, 2000);
			return () => clearTimeout(timer);
		}
		return undefined;
	}, [isAutoSaveEnabled, isDirty, handleSave]);

	const [viewMode, setViewMode] = useState<"editor" | "preview" | "split">(
		window.innerWidth > 1024 ? "split" : "editor",
	);

	if (isLoading) return <div className="loading">Loading resume...</div>;
	if (error || !localResume)
		return <div className="error">Error loading resume.</div>;

	const handlePrint = () => window.print();

	const onBack = () => navigate("/");

	const handleSettingsSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		mutation.mutate({
			name: editName,
			slug: editSlug,
			isPublished: editIsPublished,
		});
		setIsSettingsOpen(false);
	};

	const openSettings = () => {
		setEditName(localResume.name);
		setEditSlug(localResume.slug);
		setEditIsPublished(localResume.isPublished || false);
		setIsSettingsOpen(true);
	};

	const updateLocalResume = (updates: Partial<Resume>) => {
		setLocalResume((prev) => (prev ? { ...prev, ...updates } : null));
		setIsDirty(true);
	};

	const handleDragEnd = (
		event: DragEndEvent,
		field: keyof Pick<Resume, "experience" | "education" | "projects">,
	) => {
		const { active, over } = event;

		if (over && active.id !== over.id) {
			const items = localResume[field] as { id: string }[];
			const oldIndex = items.findIndex((item) => item.id === active.id);
			const newIndex = items.findIndex((item) => item.id === over.id);

			const newItems = arrayMove(items, oldIndex, newIndex);
			updateLocalResume({ [field]: newItems });
		}
	};

	const updatePersonal = (field: keyof Resume["personal"], value: string) => {
		updateLocalResume({
			personal: { ...localResume.personal, [field]: value },
		});
	};

	const addExperience = () => {
		const newExp = {
			id: crypto.randomUUID(),
			role: "New Role",
			company: "Company",
			location: "Location",
			startDate: "",
			endDate: "",
			description: "Description...",
		};
		updateLocalResume({ experience: [newExp, ...localResume.experience] });
	};

	const updateExperience = (id: string, field: string, value: string) => {
		updateLocalResume({
			experience: localResume.experience.map((exp) =>
				exp.id === id ? { ...exp, [field]: value } : exp,
			),
		});
	};

	const removeExperience = (id: string) => {
		setDeleteAction({ id, type: "experience" });
		setIsDeleteModalOpen(true);
	};

	const addEducation = () => {
		const newEdu = {
			id: crypto.randomUUID(),
			school: "School Name",
			degree: "Degree",
			year: "Year",
		};
		updateLocalResume({ education: [...localResume.education, newEdu] });
	};

	const updateEducation = (id: string, field: string, value: string) => {
		updateLocalResume({
			education: localResume.education.map((edu) =>
				edu.id === id ? { ...edu, [field]: value } : edu,
			),
		});
	};

	const removeEducation = (id: string) => {
		setDeleteAction({ id, type: "education" });
		setIsDeleteModalOpen(true);
	};

	const addProject = () => {
		const newProject = {
			id: crypto.randomUUID(),
			name: "Project Name",
			description: "Project Description",
			link: "",
			technologies: [],
		};
		updateLocalResume({
			projects: [...(localResume.projects || []), newProject],
		});
	};

	const updateProject = (id: string, field: string, value: string) => {
		updateLocalResume({
			projects: (localResume.projects || []).map((p) =>
				p.id === id ? { ...p, [field]: value } : p,
			),
		});
	};

	const removeProject = (id: string) => {
		setDeleteAction({ id, type: "projects" });
		setIsDeleteModalOpen(true);
	};

	const addCertification = () => {
		const newCert = {
			id: crypto.randomUUID(),
			name: "Certification Name",
			issuer: "Issuer",
			year: "Year",
		};
		updateLocalResume({
			certifications: [...(localResume.certifications || []), newCert],
		});
	};

	const updateCertification = (id: string, field: string, value: string) => {
		updateLocalResume({
			certifications: (localResume.certifications || []).map((c) =>
				c.id === id ? { ...c, [field]: value } : c,
			),
		});
	};

	const removeCertification = (id: string) => {
		setDeleteAction({ id, type: "certifications" });
		setIsDeleteModalOpen(true);
	};

	const addSkill = () => {
		setNewSkillName("");
		setIsSkillModalOpen(true);
	};

	const handleSkillSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (newSkillName.trim()) {
			updateLocalResume({
				skills: [
					...localResume.skills,
					{ id: crypto.randomUUID(), name: newSkillName.trim() },
				],
			});
			setIsSkillModalOpen(false);
			setNewSkillName("");
		}
	};

	const removeSkill = (id: string) => {
		setDeleteAction({ id, type: "skills" });
		setIsDeleteModalOpen(true);
	};

	const handleConfirmDelete = () => {
		if (!deleteAction) return;

		const { id, type } = deleteAction;
		if (type === "experience") {
			updateLocalResume({
				experience: localResume.experience.filter((exp) => exp.id !== id),
			});
		} else if (type === "education") {
			updateLocalResume({
				education: localResume.education.filter((edu) => edu.id !== id),
			});
		} else if (type === "projects") {
			updateLocalResume({
				projects: (localResume.projects || []).filter((p) => p.id !== id),
			});
		} else if (type === "certifications") {
			updateLocalResume({
				certifications: (localResume.certifications || []).filter(
					(c) => c.id !== id,
				),
			});
		} else if (type === "skills") {
			updateLocalResume({
				skills: localResume.skills.filter((s) => s.id !== id),
			});
		}

		setIsDeleteModalOpen(false);
		setDeleteAction(null);
	};

	const toggleAutoSave = () => {
		setIsAutoSaveEnabled((prev) => {
			const next = !prev;
			localStorage.setItem("resume-autosave-enabled", String(next));
			return next;
		});
	};

	return (
		<div className={`app-container view-mode-${viewMode}`}>
			<header className="app-header">
				<div className="header-left">
					<Button variant="outline" className="btn-back" onClick={onBack}>
						<ChevronLeft size={16} />
					</Button>
					<div>
						<h1 className="app-title">
							{localResume.name}
							<button
								type="button"
								className="action-btn"
								onClick={openSettings}
								title="Rename / Change Slug"
							>
								<Settings size={14} />
							</button>
							{isDirty && <span className="dirty-indicator" />}
						</h1>
						{mutation.isPending && (
							<span className="saving-indicator">Saving...</span>
						)}
					</div>
				</div>

				<div className="view-switcher">
					<button
						title="Editor Mode"
						type="button"
						className={`switcher-btn ${viewMode === "editor" ? "active" : ""}`}
						onClick={() => setViewMode("editor")}
					>
						✏️
					</button>
					<button
						title="Split View"
						type="button"
						className={`switcher-btn desktop-only ${viewMode === "split" ? "active" : ""}`}
						onClick={() => setViewMode("split")}
					>
						📖
					</button>
					<button
						title="Preview Mode"
						type="button"
						className={`switcher-btn ${viewMode === "preview" ? "active" : ""}`}
						onClick={() => setViewMode("preview")}
					>
						👁
					</button>
				</div>

				<div className="header-actions">
					<button
						className={`auto-save-toggle ${isAutoSaveEnabled ? "active" : ""}`}
						onClick={toggleAutoSave}
						title={isAutoSaveEnabled ? "Disable auto-save" : "Enable auto-save"}
						type="button"
					>
						{isAutoSaveEnabled ? <Cloud size={18} /> : <CloudOff size={18} />}
						<span className="auto-save-label desktop-only">Auto-save</span>
					</button>

					<Button
						variant="outline"
						onClick={handleSave}
						disabled={mutation.isPending || !isDirty}
						title="Manual Save"
						style={{ height: "36px", padding: "0 12px" }}
					>
						<Save size={16} />
						<span className="desktop-only" style={{ marginLeft: "8px" }}>
							Save
						</span>
					</Button>

					<Button type="button" onClick={handlePrint} className="btn-pdf">
						<Download size={16} /> PDF
					</Button>
				</div>
			</header>

			<div className="main-editor-area">
				{/* --- Sidebar / Editor --- */}
				<div className="sidebar">
					<div className="editor-content">
						<SectionHeader title="Personal Details" icon={User} />
						<div className="form-grid">
							<div className="col-span-2">
								<InputGroup
									label="Full Name"
									value={localResume.personal.name}
									onChange={(v) => updatePersonal("name", v)}
								/>
							</div>
							<div className="col-span-2">
								<InputGroup
									label="Job Title"
									value={localResume.personal.title}
									onChange={(v) => updatePersonal("title", v)}
								/>
							</div>
							<InputGroup
								label="Email"
								value={localResume.personal.email}
								onChange={(v) => updatePersonal("email", v)}
							/>
							<InputGroup
								label="Phone"
								value={localResume.personal.phone}
								onChange={(v) => updatePersonal("phone", v)}
							/>
							<InputGroup
								label="Location"
								value={localResume.personal.location}
								onChange={(v) => updatePersonal("location", v)}
							/>
							<InputGroup
								label="LinkedIn"
								value={localResume.personal.linkedin}
								onChange={(v) => updatePersonal("linkedin", v)}
							/>
							<InputGroup
								label="GitHub"
								value={localResume.personal.github || ""}
								onChange={(v) => updatePersonal("github", v)}
							/>
							<InputGroup
								label="Website"
								value={localResume.personal.website || ""}
								onChange={(v) => updatePersonal("website", v)}
							/>
						</div>
						<TextAreaGroup
							label="Professional Summary"
							value={localResume.personal.summary}
							onChange={(v) => updatePersonal("summary", v)}
						/>

						<ExperienceEditor
							experience={localResume.experience}
							onAdd={addExperience}
							onUpdate={updateExperience}
							onRemove={removeExperience}
							onDragEnd={(e) => handleDragEnd(e, "experience")}
							sensors={sensors}
						/>

						<ProjectEditor
							projects={localResume.projects}
							onAdd={addProject}
							onUpdate={updateProject}
							onRemove={removeProject}
							onDragEnd={(e) => handleDragEnd(e, "projects")}
							sensors={sensors}
						/>

						<EducationEditor
							education={localResume.education}
							onAdd={addEducation}
							onUpdate={updateEducation}
							onRemove={removeEducation}
							onDragEnd={(e) => handleDragEnd(e, "education")}
							sensors={sensors}
						/>

						<CertificationEditor
							certifications={localResume.certifications}
							onAdd={addCertification}
							onUpdate={updateCertification}
							onRemove={removeCertification}
						/>

						<SkillEditor
							skills={localResume.skills}
							onAdd={addSkill}
							onRemove={removeSkill}
						/>
					</div>
				</div>

				{/* --- Main Content / Preview --- */}
				<ResumePreview resume={localResume} />
			</div>

			<Modal
				isOpen={isSettingsOpen}
				onClose={() => setIsSettingsOpen(false)}
				title="Resume Settings"
			>
				<form onSubmit={handleSettingsSubmit} className="modal-form">
					<div className="form-group">
						<label htmlFor="edit-name" className="form-label">
							Resume Name
						</label>
						<input
							id="edit-name"
							className="form-control"
							value={editName}
							onChange={(e) => setEditName(e.target.value)}
							required
						/>
					</div>
					<div className="form-group">
						<label htmlFor="edit-slug" className="form-label">
							URL Slug
						</label>
						<input
							id="edit-slug"
							className="form-control"
							value={editSlug}
							onChange={(e) => setEditSlug(e.target.value)}
							required
						/>
					</div>
					<div
						className="form-group"
						style={{ flexDirection: "row", gap: "10px" }}
					>
						<input
							id="edit-is-published"
							type="checkbox"
							checked={editIsPublished}
							onChange={(e) => setEditIsPublished(e.target.checked)}
						/>
						<label htmlFor="edit-is-published" className="form-label">
							Publicly accessible
						</label>
					</div>
					<div
						style={{
							marginTop: "2rem",
							display: "flex",
							justifyContent: "flex-end",
							gap: "1rem",
						}}
					>
						<Button variant="outline" onClick={() => setIsSettingsOpen(false)}>
							Cancel
						</Button>
						<Button type="submit">Save Changes</Button>
					</div>
				</form>
			</Modal>

			<Modal
				isOpen={isSkillModalOpen}
				onClose={() => setIsSkillModalOpen(false)}
				title="Add New Skill"
			>
				<form onSubmit={handleSkillSubmit} className="modal-form">
					<div className="form-group">
						<label htmlFor="skill-name" className="form-label">
							Skill Name (e.g., "React", "Project Management")
						</label>
						<input
							id="skill-name"
							className="form-control"
							value={newSkillName}
							onChange={(e) => setNewSkillName(e.target.value)}
							placeholder="Enter skill..."
							required
						/>
					</div>
					<div
						style={{
							marginTop: "2rem",
							display: "flex",
							justifyContent: "flex-end",
							gap: "1rem",
						}}
					>
						<Button
							variant="outline"
							onClick={() => setIsSkillModalOpen(false)}
						>
							Cancel
						</Button>
						<Button type="submit">Add Skill</Button>
					</div>
				</form>
			</Modal>

			<Modal
				isOpen={isDeleteModalOpen}
				onClose={() => setIsDeleteModalOpen(false)}
				title="Confirm Deletion"
			>
				<div className="modal-body-confirm">
					<p>
						Are you sure you want to remove this {deleteAction?.type}? This
						action cannot be undone.
					</p>
					<div
						style={{
							marginTop: "2rem",
							display: "flex",
							justifyContent: "flex-end",
							gap: "1rem",
						}}
					>
						<Button
							variant="outline"
							onClick={() => setIsDeleteModalOpen(false)}
						>
							Cancel
						</Button>
						<Button
							variant="primary"
							onClick={handleConfirmDelete}
							style={{ backgroundColor: "#ef4444", borderColor: "#ef4444" }}
						>
							Remove
						</Button>
					</div>
				</div>
			</Modal>
		</div>
	);
}
